import { useCallback, useEffect, useRef, useState } from 'react'
import { randomSeed, type BuildItem, type BuildResponse, type Tag } from '@/components/build/types'

export interface BuildParams {
  instructorId: string
  discipline: string
  minutes: number
  theme: string | null
  format: string | null
}

// One row of the setlist. `id` is the row's identity for drag-and-drop and
// animation (it follows the row when it's dragged); position in the array is
// what decides its role and time.
export interface Entry {
  id: string
  item: BuildItem | null
  alternatives: BuildItem[]
  locked: boolean
}

export interface Frame {
  role: string
  startsAt: number
  endsAt: number
}

export interface BuildMeta {
  songCount: number
  ridesConsidered: number
  pool: Partial<Record<Tag, number>>
}

const DEBOUNCE_MS = 220

// Owns the whole interactive board: the server-built ride, plus everything
// the person does to it -- lock, swap, remove, drag to reorder, turn the
// adventure dial, re-roll. Only the *unlocked* slots are ever regenerated.
export function usePlaylistBuild(params: BuildParams, initial: { seed: number; adventure: number }) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [entries, setEntries] = useState<Entry[]>([])
  const [frame, setFrame] = useState<Frame[]>([])
  const [meta, setMeta] = useState<BuildMeta | null>(null)
  const [seed, setSeed] = useState(initial.seed)
  const [adventure, setAdventureState] = useState(initial.adventure)
  const [regenerating, setRegenerating] = useState(false)

  const seedRef = useRef(initial.seed)
  const adventureRef = useRef(initial.adventure)
  const entriesRef = useRef<Entry[]>([])
  const rejected = useRef<Set<string>>(new Set()) // songs the person removed: never bring them back
  const requestId = useRef(0)
  const idCounter = useRef(0)
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  entriesRef.current = entries

  const { instructorId, discipline, minutes, theme, format } = params

  const generate = useCallback(async () => {
    const id = ++requestId.current
    setRegenerating(true)

    const locked: Record<number, string> = {}
    entriesRef.current.forEach((entry, index) => {
      if (entry.locked && entry.item) locked[index] = entry.item.key
    })
    const query = new URLSearchParams({
      instructor_id: instructorId,
      discipline,
      minutes: String(minutes),
      adventure: String(adventureRef.current),
      seed: String(seedRef.current),
      locked: JSON.stringify(locked),
      excluded: JSON.stringify([...rejected.current]),
    })
    if (theme) query.set('theme', theme)
    if (format) query.set('format', format)

    try {
      const response = await fetch(`/api/playlist_builder/generate?${query}`, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error(`Request failed: ${response.status}`)
      const data = (await response.json()) as BuildResponse
      if (id !== requestId.current) return // a newer request superseded this one

      setFrame(data.slots.map((slot) => ({ role: slot.role, startsAt: slot.starts_at, endsAt: slot.ends_at })))
      setMeta({ songCount: data.song_count, ridesConsidered: data.rides_considered, pool: data.pool })
      setEntries((previous) =>
        data.slots.map((slot, index) => {
          const old = previous[index]
          // A locked row that came back unchanged keeps its identity, so it doesn't re-animate.
          if (slot.locked && old?.locked && old.item?.key === slot.pick?.key) return { ...old, alternatives: slot.alternatives }
          return { id: `e${idCounter.current++}`, item: slot.pick, alternatives: slot.alternatives, locked: slot.locked }
        }),
      )
      setStatus('ready')
    } catch {
      if (id === requestId.current) setStatus('error')
    } finally {
      if (id === requestId.current) setRegenerating(false)
    }
  }, [instructorId, discipline, minutes, theme, format])

  // A different setup starts a fresh ride.
  useEffect(() => {
    rejected.current = new Set()
    entriesRef.current = []
    setEntries([])
    setStatus('loading')
    void generate()
    return () => clearTimeout(debounce.current)
  }, [generate])

  const setAdventure = useCallback(
    (value: number) => {
      adventureRef.current = value
      setAdventureState(value)
      clearTimeout(debounce.current)
      debounce.current = setTimeout(() => void generate(), DEBOUNCE_MS)
    },
    [generate],
  )

  const shuffle = useCallback(() => {
    const next = randomSeed()
    seedRef.current = next
    setSeed(next)
    void generate()
  }, [generate])

  const toggleLock = useCallback((id: string) => {
    setEntries((previous) => previous.map((entry) => (entry.id === id && entry.item ? { ...entry, locked: !entry.locked } : entry)))
  }, [])

  // Put an alternative in a row; the song it replaces becomes an alternative.
  const swap = useCallback((id: string, choice: BuildItem) => {
    setEntries((previous) =>
      previous.map((entry) => {
        if (entry.id === id) {
          const back = entry.item ? [entry.item] : []
          return { ...entry, item: choice, locked: false, alternatives: [...back, ...entry.alternatives.filter((a) => a.key !== choice.key)] }
        }
        return { ...entry, alternatives: entry.alternatives.filter((a) => a.key !== choice.key) } // it's used now
      }),
    )
  }, [])

  const remove = useCallback((id: string) => {
    setEntries((previous) =>
      previous.map((entry) => {
        if (entry.id !== id || !entry.item) return entry
        rejected.current.add(entry.item.key)
        return { ...entry, item: null, locked: false }
      }),
    )
  }, [])

  const reorder = useCallback((next: Entry[]) => setEntries(next), [])

  return { status, regenerating, entries, frame, meta, seed, adventure, setAdventure, shuffle, toggleLock, swap, remove, reorder }
}
